// components/ui/RichTextEditor.tsx
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import { Bold, Italic, List, ListOrdered, Heading1, Heading2, Undo, Redo, Image as ImageIcon } from "lucide-react";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onImageUpload?: (file: File) => Promise<string>;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = "Écrivez votre contenu ici...",
  onImageUpload,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder }),
      Image.configure({ inline: true, allowBase64: false }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: { class: "ProseMirror" },
    },
  });

  if (!editor) return null;

  const addImage = async () => {
    if (!onImageUpload) {
      alert("Upload d'images non configuré");
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        const url = await onImageUpload(file);
        console.log(url);
        editor.chain().focus().setImage({ src: url }).run();
      } catch (error) {
        alert("Erreur lors de l'upload");
      }
    };
    input.click();
  };

  const toolbarButton = (
    onClick: () => void,
    isActive = false,
    disabled = false,
    title: string,
    children: React.ReactNode
  ) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${isActive ? "bg-gray-200 text-primary" : ""} disabled:opacity-50`}
      title={title}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 bg-gray-50">
        {toolbarButton(
          () => editor.chain().focus().undo().run(),
          false,
          !editor.can().undo(),
          "Annuler",
          <Undo size={16} />
        )}
        {toolbarButton(
          () => editor.chain().focus().redo().run(),
          false,
          !editor.can().redo(),
          "Rétablir",
          <Redo size={16} />
        )}

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {toolbarButton(
          () => editor.chain().focus().toggleBold().run(),
          editor.isActive("bold"),
          false,
          "Gras",
          <Bold size={16} />
        )}
        {toolbarButton(
          () => editor.chain().focus().toggleItalic().run(),
          editor.isActive("italic"),
          false,
          "Italique",
          <Italic size={16} />
        )}

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {toolbarButton(
          () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
          editor.isActive("heading", { level: 1 }),
          false,
          "Titre H1",
          <Heading1 size={16} />
        )}
        {toolbarButton(
          () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
          editor.isActive("heading", { level: 2 }),
          false,
          "Titre H2",
          <Heading2 size={16} />
        )}

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {toolbarButton(
          () => editor.chain().focus().toggleBulletList().run(),
          editor.isActive("bulletList"),
          false,
          "Liste à puces",
          <List size={16} />
        )}
        {toolbarButton(
          () => editor.chain().focus().toggleOrderedList().run(),
          editor.isActive("orderedList"),
          false,
          "Liste numérotée",
          <ListOrdered size={16} />
        )}

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {toolbarButton(addImage, false, false, "Ajouter une image", <ImageIcon size={16} />)}
      </div>

      <EditorContent editor={editor} />
    </div>
  );
}
